import {faFileArchive} from "@fortawesome/free-solid-svg-icons";
import {boundMethod} from "autobind-decorator";
import {debounce} from "lodash";
import React from "react";

import {EServerId} from "@shared/models";
import {MUCH_LONGER_DELAY} from "@toolbox/models";
import {ILocalizedText} from "@translate/models";
import {EDocIdPrefixes} from "../models";
import {
    EMissingExport,
    IDocType,
    IExportRequest,
    IExportResponse,
    IPartialSelectedItem,
} from "./models";

import DropdownExport from "@shared/sample/DropdownExport";
import FormSubmit from "@toolbox/button-like/FormSubmit";
import {bitwiseAND} from "@toolbox/functions/bitwise";
import T, {intl2Str} from "@translate/T";

export interface IExportButtonProps {
    idSuffix: string;
    project: number;

    name: string;
    selection: IPartialSelectedItem[];

    // for measurement & analysis exports only
    sampleIds?: string[];
    serverId?: EServerId;

    className?: string;
    classNameBelow?: string;
    disabled?: boolean;
}

interface IExportButtonState {
    idUrl: string;
    warning: EMissingExport;
}

class ExportButton extends React.PureComponent<
    IExportButtonProps,
    IExportButtonState
> {
    public readonly state: IExportButtonState = {
        idUrl: "",
        warning: EMissingExport.None,
    };

    private readonly reset = debounce(
        () => this.setState({warning: EMissingExport.None}),
        MUCH_LONGER_DELAY,
    );

    public componentWillUnmount() {
        this.reset.cancel();
    }

    @boundMethod
    public async onChange(response: Response, after: () => void) {
        const resolve = (await response.json()) as IExportResponse;

        this.reset.cancel();
        this.setState(
            {idUrl: "/" + resolve.id, warning: resolve.missing},
            () => {
                after();
                this.setState({idUrl: ""});
                this.reset();
            },
        );
    }

    @boundMethod
    public async getJson() {
        const {project, name, selection} = this.props;

        const json: IExportRequest = {project, name, doc: {}};

        for (const item of selection) {
            const field = this.getExportField(item);
            if (!field) {
                continue;
            }

            if (!json.doc[field]) {
                json.doc[field] = [];
            }

            json.doc[field]!.push(item.id);
        }

        return json;
    }

    public render() {
        const {
            className,
            classNameBelow,
            disabled,
            idSuffix,
            children,
            serverId,
            sampleIds,
        } = this.props;
        const {idUrl} = this.state;
        const id = "export-docs-" + idSuffix;
        const title: ILocalizedText = (intl) =>
            intl2Str(
                intl,
                "Export to LUM proprietary file format suitable for import by SEPView.",
            );

        return (
            <div className={"btn-group" + (className ? " " + className : "")}>
                <FormSubmit
                    action={"/api/export/docs" + idUrl}
                    className={
                        "btn btn-primary" +
                        (classNameBelow ? " " + classNameBelow : "")
                    }
                    disabled={disabled}
                    icon={faFileArchive}
                    method="GET"
                    name={<T>Export</T>}
                    idSuffix={id}
                    title={title}
                    warningXerror={this.getTooltip()}
                    getJson={this.getJson}
                    onChange={this.onChange}
                />
                <DropdownExport
                    className={classNameBelow}
                    disabled={disabled}
                    noAuc={idSuffix === "analysis"}
                    sampleIds={sampleIds}
                    serverId={serverId}
                    storeApi="/api/export/docs/store"
                    getJson={this.getJson}
                >
                    {children}
                </DropdownExport>
            </div>
        );
    }

    private getExportField(item: IPartialSelectedItem): IDocType {
        switch (item.prefix) {
            case EDocIdPrefixes.CentMeasures:
            case EDocIdPrefixes.CentSamples:
                return "centrifugeMeasurements";

            case EDocIdPrefixes.FracMeasures:
            case EDocIdPrefixes.FracSamples:
                return "fractureMeasurements";

            case EDocIdPrefixes.SpocMeasures:
            case EDocIdPrefixes.SpocSamples:
                return "spocMeasurements";

            case EDocIdPrefixes.SpocAlignments:
                return "spocAlignments";

            case EDocIdPrefixes.CentAnalyses:
                return "centrifugeAnalyses";

            case EDocIdPrefixes.FracAnalyses:
                return "fractureAnalyses";

            case EDocIdPrefixes.SpocAnalyses:
                return "spocAnalyses";

            case EDocIdPrefixes.CentSops:
            case EDocIdPrefixes.CentSopQueues:
                return "centrifugeSops";

            case EDocIdPrefixes.FracSops:
                return "fractureSops";

            case EDocIdPrefixes.SpocSops:
                return "spocSops";

            case EDocIdPrefixes.CentReports:
            case EDocIdPrefixes.SpocReports:
                return "reports";
        }
    }

    private getTooltip() {
        const {warning} = this.state;
        if (warning === EMissingExport.None) {
            return undefined;
        }

        const fragment = (item: JSX.Element) => (
            <React.Fragment>
                <br />
                {item}
            </React.Fragment>
        );

        return (
            <span className="text-warning">
                <T>
                    Exported document is corrupted. Import might fail. Please
                    contact service with support data provided.
                </T>
                {!!bitwiseAND(warning, EMissingExport.Normalization) &&
                    fragment(<T>Normalization is missing.</T>)}
                {!!bitwiseAND(warning, EMissingExport.Material) &&
                    fragment(<T>Material is missing.</T>)}
                {!!bitwiseAND(warning, EMissingExport.Stamp) &&
                    fragment(<T>Stamp is missing.</T>)}
                {!!bitwiseAND(warning, EMissingExport.Sample) &&
                    fragment(<T>Sample is missing.</T>)}
            </span>
        );
    }
}

export default ExportButton;
